/**
 * Product Linker API
 *
 * Request helpers and shared types for post product linking.
 */

import { ApiBase } from "../../core/api-base";
import { z } from "zod";
import { GetItem200Response } from "../../api/generated/products.zod";

export type ProductResponseEnvelope = z.infer<typeof GetItem200Response>;
export type ProductLinkerApiProduct = NonNullable<NonNullable<ProductResponseEnvelope["data"]>["item"]>;

export type ProductLinkerProduct = ProductLinkerApiProduct & {
    brand?: {
        brand_id?: number;
        brand_name?: string;
    };
    image?: string;
    image_path?: string;
    category?: {
        category_id?: number;
        category_name?: string;
        parent_category_id?: number | null;
        parent_category_name?: string | null;
    };
    recommendRank?: number | null;
};

class ProductLinkerApi extends ApiBase {
    private static cache: Map<string, ProductLinkerProduct> = new Map();

    private static inFlight: Map<string, Promise<ProductLinkerProduct | null>> = new Map();

    public static async fetchProduct(productId: number): Promise<ProductLinkerProduct | null> {
        if (!Number.isInteger(productId) || productId <= 0) return null;

        const normalizedProductId = String(productId);

        if (this.cache.has(normalizedProductId)) {
            return this.cache.get(normalizedProductId) || null;
        }

        const inFlightRequest = this.inFlight.get(normalizedProductId);
        if (inFlightRequest) {
            return inFlightRequest;
        }

        const fetchProductRequest = (async () => {
            try {
                const productResponse = await super.apiGet(
                    `/api/v1/products/${encodeURIComponent(normalizedProductId)}`,
                    {
                        noSpinner: true,
                    },
                );

                if (!productResponse.ok) {
                    return null;
                }

                const productResponseBody = (await productResponse.json()) as ProductResponseEnvelope;
                if (productResponseBody?.success !== 1) {
                    return null;
                }

                const fetchedProduct = productResponseBody?.data?.item ?? null;
                if (fetchedProduct) {
                    this.cache.set(normalizedProductId, fetchedProduct);
                }
                return fetchedProduct;
            } finally {
                this.inFlight.delete(normalizedProductId);
            }
        })();

        this.inFlight.set(normalizedProductId, fetchProductRequest);
        return fetchProductRequest;
    }
}

export { ProductLinkerApi };
